import { create } from 'zustand';
import { currentRelativeUrl } from '~/utils/urlHelpers';

/**
 * Who asked for sign-in. 'nav' is the user badge in the header; anything else is a page action
 * such as a save button, which the user could have pressed anywhere down the page. The widget
 * presents the two differently, so it has to be told rather than guess from `reason`.
 */
export type AuthPromptSource = 'nav' | 'page';

type AuthPromptStore = {
  isOpen: boolean;
  /** Translated line explaining why we're asking, shown above the form. */
  reason?: string;
  source: AuthPromptSource;
  /**
   * Where Clerk sends the user once they're signed in — the page they were on when they asked.
   * Captured on `open` rather than read when the widget renders: `location` only moves when a
   * Next navigation commits, so a render mid-transition sees the previous page and would send
   * the user back there. The click is the one moment "where am I" is never in doubt.
   */
  returnTo?: string;
  open: (reason?: string, source?: AuthPromptSource) => void;
  close: () => void;
};

/**
 * Lets anything on the page open the navigation's sign-in widget, so there is exactly one
 * auth surface to maintain. A store rather than context because the caller (a save button
 * deep in the page) and the widget (in the header) sit in different trees.
 */
export const useAuthPromptStore = create<AuthPromptStore>((set) => ({
  isOpen: false,
  reason: undefined,
  // Defaults to 'page' so a new caller gets the safe presentation: only the badge itself knows
  // it can be pointed at.
  source: 'page',
  open: (reason, source = 'page') => set({ isOpen: true, reason, source, returnTo: currentRelativeUrl() }),
  // Only `isOpen` is reset. The panel stays mounted through its exit animation, so clearing
  // `source` here re-centred a docked panel mid-flight (it flashed to the middle on the way out)
  // and clearing `reason` changed its height. `returnTo` matters for a third reason: Clerk reads
  // the redirect as part of setActive, which can land after we've closed. `open` overwrites all
  // three every time, so nothing leaks into the next prompt.
  close: () => set({ isOpen: false })
}));
